import Api from 'Api';
import {API_GET_ACCOUNT_DETAILS} from 'Constants';
import moment from 'moment-timezone';
import {useEffect, useState} from 'react';
import {SG_TZ} from './utils';

export const TRAINING_INSTRUCTOR_ROLE = 'instructor';

export const API_COURSE_MANAGEMENT_METADATA = 'course-management/metadata';
export const API_COURSE_MANAGEMENT_INSTRUCTORS = 'course-management/instructors';

export const ADMIN_ROLES = ['admin', 'homage operator', 'dev', 'Homage Operator'];

export const TRAINING_TYPE_ACCENT_COLORS: Record<string, string> = {
  CGT: '#35d07f',
  'CPR/AED': '#f067b0',
  HPT: '#ff9f1a',
  Onboarding: '#4da7ff',
};

const hexToRgba = (hex: string, alpha: number) => {
  const normalizedHex = hex.replace('#', '');
  const value = normalizedHex.length === 3
    ? normalizedHex.split('').map(char => char + char).join('')
    : normalizedHex;
  const intValue = Number.parseInt(value, 16);
  const red = (intValue >> 16) & 255;
  const green = (intValue >> 8) & 255;
  const blue = intValue & 255;
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
};

export const TRAINING_TYPE_PILL_STYLES: Record<string, {backgroundColor: string; borderColor: string; color: string}> = {
  CGT: {
    backgroundColor: hexToRgba(TRAINING_TYPE_ACCENT_COLORS.CGT, 0.12),
    borderColor: hexToRgba(TRAINING_TYPE_ACCENT_COLORS.CGT, 0.32),
    color: TRAINING_TYPE_ACCENT_COLORS.CGT,
  },
  'CPR/AED': {
    backgroundColor: hexToRgba(TRAINING_TYPE_ACCENT_COLORS['CPR/AED'], 0.12),
    borderColor: hexToRgba(TRAINING_TYPE_ACCENT_COLORS['CPR/AED'], 0.32),
    color: TRAINING_TYPE_ACCENT_COLORS['CPR/AED'],
  },
  HPT: {
    backgroundColor: hexToRgba(TRAINING_TYPE_ACCENT_COLORS.HPT, 0.12),
    borderColor: hexToRgba(TRAINING_TYPE_ACCENT_COLORS.HPT, 0.32),
    color: TRAINING_TYPE_ACCENT_COLORS.HPT,
  },
  Onboarding: {
    backgroundColor: hexToRgba(TRAINING_TYPE_ACCENT_COLORS.Onboarding, 0.12),
    borderColor: hexToRgba(TRAINING_TYPE_ACCENT_COLORS.Onboarding, 0.32),
    color: TRAINING_TYPE_ACCENT_COLORS.Onboarding,
  },
};

export const ATTENDANCE_ATTENDED = 'Attended';
export const ATTENDANCE_NO_SHOW = 'No Show';

export const normalizeUserRoles = (role: string | string[] | undefined): string[] => {
  if (!role) {
    return [];
  }
  return Array.isArray(role) ? role : [role];
};

export const isTrainingInstructorOnly = (roles: string[]) => {
  const isAdmin = roles.some(role => ADMIN_ROLES.includes(role));
  const isInstructor = roles.includes(TRAINING_INSTRUCTOR_ROLE);
  return isInstructor && !isAdmin;
};

export const isCourseManagementAdmin = (roles: string[]) => roles.some(role => ADMIN_ROLES.includes(role));

export const isInstructorAllowedAppPath = (pathname: string) => {
  if (pathname === '/account-details' || pathname === '/course-management') {
    return true;
  }
  if (!pathname.startsWith('/course-management/')) {
    return false;
  }
  return pathname !== '/course-management/instructors'
    && pathname !== '/course-management/schedules/new';
};

export const INSTRUCTOR_CLICK_WINDOW_DAYS = 1;

export const isEventWithinInstructorWindow = (dateStart: string) => {
  const eventDay = moment.tz(dateStart, SG_TZ).startOf('day');
  const now = moment().tz(SG_TZ).startOf('day');
  const diffDays = eventDay.diff(now, 'days');
  return diffDays >= -INSTRUCTOR_CLICK_WINDOW_DAYS && diffDays <= INSTRUCTOR_CLICK_WINDOW_DAYS;
};

export const parseCourseManagementMetadata = (payload: any) => ({
  training_type_options: Array.isArray(payload?.training_type_options) ? payload.training_type_options : [],
  signature_required_training_types: Array.isArray(payload?.signature_required_training_types)
    ? payload.signature_required_training_types
    : [],
  feedback_supported_training_types: Array.isArray(payload?.feedback_supported_training_types)
    ? payload.feedback_supported_training_types
    : [],
  infection_control_training_types: Array.isArray(payload?.infection_control_training_types)
    ? payload.infection_control_training_types
    : [],
  public_holidays: Array.isArray(payload?.public_holidays) ? payload.public_holidays : [],
});

export const useCourseManagementAccess = () => {
  const [isInstructorOnly, setIsInstructorOnly] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Api.GET(
      API_GET_ACCOUNT_DETAILS,
      (response, error) => {
        if (error) {
          setIsAdmin(false);
          setIsInstructorOnly(false);
          setLoading(false);
          return;
        }

        const roles = normalizeUserRoles(response?.data?.account_details?.role);
        setIsAdmin(isCourseManagementAdmin(roles));
        setIsInstructorOnly(isTrainingInstructorOnly(roles));
        setLoading(false);
      },
      false,
      true,
      true,
    );
  }, []);

  return {isInstructorOnly, isAdmin, loading};
};
